"use client";

import { useState, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useTranslations, useLocale } from 'next-intl';
import { useLocalizedField } from '@/hooks/useLocalizedField';
import HeaderPage from "@/components/header-page";
import MonumentCard from "@/components/monument-card"; // You'll need to create this component
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup } from "@/components/ui/select";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious, PaginationEllipsis } from "@/components/ui/pagination";
import { Card, CardContent } from "@/components/ui/card";
import { Search, Filter, X, ChevronDown, ChevronUp, Landmark, MapPin, Clock, ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

const metaData = {
  meta_title_en: "Monuments of Tunisia | Explore Historical Sites",
  meta_description_en: "Discover Tunisia's magnificent monuments and historical landmarks. Explore ancient ruins, Roman architecture, and cultural heritage sites.",
  meta_keywords_en: "monuments in Tunisia, historical monuments Tunisia, archaeological sites Tunisia, Tunisian architecture, Tunisian heritage, tourist sites Tunisia, Roman ruins, ancient monuments Tunisia",
  
  meta_title_fr: "Monuments de Tunisie | Explorez les Sites Historiques",
  meta_description_fr: "Découvrez les magnifiques monuments et sites historiques de la Tunisie. Explorez les ruines antiques, l'architecture romaine et les sites du patrimoine culturel.",
  meta_keywords_fr: "monuments en Tunisie, monuments historiques Tunisie, sites archéologiques Tunisie, architecture tunisienne, patrimoine tunisien, sites touristiques Tunisie, ruines romaines, monuments anciens Tunisie",
  
  meta_title_ar: "معالم تونس | استكشف المواقع التاريخية",
  meta_description_ar: "اكتشف المعالم والمواقع التاريخية الرائعة في تونس. استكشف الآثار القديمة والعمارة الرومانية ومواقع التراث الثقافي.",
  meta_keywords_ar: "معالم تونس, معالم تاريخية تونس, مواقع أثرية تونس, العمارة التونسية, التراث التونسي, مواقع سياحية تونس, آثار رومانية, معالم قديمة تونس",
  
  meta_title_ru: "Откройте для себя главные памятники Туниса - История, Архитектура и Наследие",
  meta_description_ru: "Исследуйте знаковые памятники Туниса, являющиеся свидетельствами богатой истории и увлекательной архитектуры. Посетите исторические памятники и архитектурные чудеса по всей стране.",
  meta_keywords_ru: "памятники Туниса, исторические памятники Туниса, археологические памятники Туниса, тунисская история, наследие Туниса, туристические места Туниса, римские руины Туниса, древние памятники Туниса",
  
  meta_title_zh: "探索突尼斯必看纪念碑 - 历史、建筑与遗产",
  meta_description_zh: "探索突尼斯标志性的纪念碑，这些遗迹展现了丰富的历史和迷人的建筑艺术。参观遍布全国的历史遗址和建筑奇观。",
  meta_keywords_zh: "突尼斯纪念碑, 突尼斯历史遗迹, 突尼斯考古遗址, 突尼斯建筑, 突尼斯遗产, 突尼斯旅游景点, 罗马遗迹, 突尼斯古迹",
  
  meta_title_ja: "チュニジアの必見モニュメントを体験する – 歴史と建築遺産",
  meta_description_ja: "チュニジアの象徴的な記念碑を発見してください。豊かな歴史と魅力的な建築が国の至る所に残されています。",
  meta_keywords_ja: "チュニジア モニュメント, 歴史的建造物 チュニジア, 考古学的遺跡 チュニジア, チュニジア建築, 文化遺産 チュニジア, チュニジア観光スポット, ローマ時代遺跡, 古代遺跡 チュニジア",
  
  meta_title_ko: "튀니지의 필수 관광 명소 — 역사, 건축, 유산",
  meta_description_ko: "튀니지의 상징적인 기념물을 탐방해 보세요. 풍부한 역사와 매력적인 건축물들이 전국 곳곳에 자리하고 있습니다.",
  meta_keywords_ko: "튀니지 기념물, 튀니지 역사 유적지, 튀니지 고고학적 유적, 튀니지 건축물, 튀니지 문화유산, 튀니지 관광지, 로마 유적지, 튀니지 고대 유적",
};



interface PaginationLink {
  url: string | null;
  label: string;
  active: boolean;
}

interface MonumentApiResponse {
  monument: {
    current_page: number;
    data: any[];
    first_page_url: string;
    from: number;
    last_page: number;
    last_page_url: string;
    links: PaginationLink[];
    next_page_url: string | null;
    path: string;
    per_page: number;
    prev_page_url: string | null;
    to: number;
    total: number;
  };
}

interface FilterOptions {
  destinations: any[];
}

export default function MonumentsClient() {
  const t = useTranslations();
  const locale = useLocale();
  const { getLocalizedField } = useLocalizedField();
  const router = useRouter();
  const searchParams = useSearchParams();
  
  // State for monuments data
  const [monuments, setMonuments] = useState<any[]>([]);
  const [paginationData, setPaginationData] = useState<MonumentApiResponse['monument'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingFilters, setLoadingFilters] = useState(true);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDestination, setSelectedDestination] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  // Filter options
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    destinations: [],
  });

  useEffect(() => {
    const baseUrl = process.env.NEXT_PUBLIC_BASE_URL || 'https://tunisiagotravel.com';
    
    console.log('=== Metadata Debug ===');
    console.log('Current locale:', locale);
    console.log('Available keys:', Object.keys(metaData));
    
    const titleKey = `meta_title_${locale}`;
    const descKey = `meta_description_${locale}`;
    const keywordsKey = `meta_keywords_${locale}`;
    
    console.log('Looking for title key:', titleKey);
    console.log('Title value:', (metaData as any)[titleKey]);
    
    const title = (metaData as any)[titleKey] || metaData.meta_title_en;
    const description = (metaData as any)[descKey] || metaData.meta_description_en;
    const keywords = (metaData as any)[keywordsKey] || metaData.meta_keywords_en;
    
    console.log('Final title:', title);
    console.log('Final description:', description);
    
    document.title = title;
    
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', description);
    
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', keywords);
    
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${baseUrl}/${locale}/monuments`);
  }, [locale]);



  // Build query parameters for API
  const buildQueryParams = () => {
    const params = new URLSearchParams();
    params.append("page", currentPage.toString());
    
    if (searchQuery) params.append("search", searchQuery);
    if (selectedDestination !== "all") {
      params.append("destination_id", selectedDestination);
    }
    
    return params;
  };

  // Build URL parameters for UI
  const buildUrlParams = () => {
    const params = new URLSearchParams();
    
    if (currentPage !== 1) params.append("page", currentPage.toString());
    if (searchQuery) params.append("search", searchQuery);
    if (selectedDestination !== "all") params.append("destination", selectedDestination);
    
    return params;
  };

  // Update URL when filters change
  const updateUrl = () => {
    if (isInitialLoad) return;
    
    const params = buildUrlParams();
    const queryString = params.toString();
    const newUrl = queryString ? `/monuments?${queryString}` : '/monuments';
    
    router.push(newUrl, { scroll: false });
  };

  // Fetch filter options (destinations only)
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        // Fetch destinations
        const destinationsResponse = await fetch(`${process.env.NEXT_PUBLIC_API_URL}alldestinations`);
        const destinationsData = await destinationsResponse.json();
        
        const destinationsList: any[] = destinationsData.destinations || [];
        const sortedDestinations = destinationsList.sort((a, b) => 
          a.name.localeCompare(b.name)
        );
        
        setFilterOptions({
          destinations: sortedDestinations,
        });
        setLoadingFilters(false);
      } catch (error) {
        console.error("Error fetching filter options:", error);
        setLoadingFilters(false);
      }
    };
    
    fetchFilterOptions();
  }, []);

  // Initialize state from URL params
  useEffect(() => {
    if (!searchParams || !isInitialLoad) return;
    
    const pageParam = searchParams.get("page");
    const searchParam = searchParams.get("search");
    const destinationParam = searchParams.get("destination");
    
    if (pageParam) {
      const page = parseInt(pageParam);
      if (!isNaN(page) && page > 0) {
        setCurrentPage(page);
      }
    }
    
    if (searchParam) setSearchQuery(searchParam);
    if (destinationParam) setSelectedDestination(destinationParam);
    
    setIsInitialLoad(false);
  }, [searchParams, isInitialLoad]);

  // Update URL when filters change
  useEffect(() => {
    updateUrl();
  }, [currentPage, searchQuery, selectedDestination]);

  // Fetch monuments based on filters
  useEffect(() => {
    const fetchMonuments = async () => {
      if (isInitialLoad || loadingFilters) return;
      
      setLoading(true);
      try {
        const apiParams = buildQueryParams();
        
        // Fetch data from monuments API endpoint
        const apiUrl = `${process.env.NEXT_PUBLIC_API_URL}monument?${apiParams.toString()}`;
        const response = await fetch(apiUrl);
        const data: MonumentApiResponse = await response.json();
        
        setMonuments(data.monument.data);
        setPaginationData(data.monument);
      } catch (error) {
        console.error("Error fetching monuments:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchMonuments();
  }, [currentPage, searchQuery, selectedDestination, isInitialLoad, loadingFilters]);

  // Handle search
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    setShowMobileFilters(false);
  };

  // Handle filter changes
  const handleDestinationChange = (value: string) => {
    setSelectedDestination(value);
    setCurrentPage(1);
    setShowMobileFilters(false);
  };

  // Handle pagination
  const handlePageChange = (url: string | null) => {
    if (!url) return;
    
    try {
      const urlObj = new URL(url);
      const pageParam = urlObj.searchParams.get("page");
      if (pageParam) {
        const page = parseInt(pageParam);
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch (error) {
      console.error("Error parsing page URL:", error);
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery("");
    setSelectedDestination("all");
    setCurrentPage(1);
    setShowMobileFilters(false);
    
    router.push('/monuments', { scroll: false });
  };

  // Clear single filters
  const clearSearch = () => {
    setSearchQuery("");
    setCurrentPage(1);
  };

  const clearDestination = () => {
    setSelectedDestination("all");
    setCurrentPage(1);
  };

  // Get selected names for display
  const getSelectedDestinationName = () => {
    if (selectedDestination === "all") return null;
    const destination = filterOptions.destinations.find(d => d.slug === selectedDestination);
    return destination ? destination.name : selectedDestination;
  };

  // Function to render pagination links
  const renderPaginationLinks = () => {
    if (!paginationData || !paginationData.links) return null;

    return paginationData.links.map((link, index) => {
      if (link.label === "...") {
        return (
          <PaginationItem key={index}>
            <PaginationEllipsis />
          </PaginationItem>
        );
      }

      const isPrevious = link.label.includes("previous");
      const isNext = link.label.includes("next");
      
      if (isPrevious) {
        return (
          <PaginationItem key={index}>
            <PaginationLink
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handlePageChange(paginationData?.prev_page_url || null);
              }}
              className={`gap-2 px-3 sm:px-4 py-2 min-w-[100px] min-h-[44px] flex items-center justify-center ${
                !paginationData?.prev_page_url
                  ? "pointer-events-none opacity-50"
                  : ""
              }`}
            >
              {locale === "ar" ? (
                <>
                  <span className="hidden sm:block text-xs sm:text-sm">
                    {t("common.previous")}
                  </span>
                  <ChevronLeftIcon className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                </>
              ) : (
                <>
                  <ChevronLeftIcon className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                  <span className="hidden sm:block text-xs sm:text-sm">
                    {t("common.previous")}
                  </span>
                </>
              )}
            </PaginationLink>
          </PaginationItem>
        );
      }

      if (isNext) {
        return (
          <PaginationItem key={index}>
            <PaginationLink
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handlePageChange(paginationData?.next_page_url || null);
              }}
              className={`gap-2 px-3 sm:px-4 py-2 min-w-[100px] min-h-[44px] flex items-center justify-center ${
                !paginationData?.next_page_url
                  ? "pointer-events-none opacity-50"
                  : ""
              }`}
            >
              {locale === "ar" ? (
                <>
                  <ChevronRightIcon className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                  <span className="hidden sm:block text-xs sm:text-sm">
                    {t("common.next")}
                  </span>
                </>
              ) : (
                <>
                  <span className="hidden sm:block text-xs sm:text-sm">
                    {t("common.next")}
                  </span>
                  <ChevronRightIcon className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                </>
              )}
            </PaginationLink>
          </PaginationItem>
        );
      }

      const pageNum = parseInt(link.label);
      if (!isNaN(pageNum)) {
        return (
          <PaginationItem key={index}>
            <PaginationLink
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handlePageChange(link.url);
              }}
              isActive={link.active}
            >
              {pageNum}
            </PaginationLink>
          </PaginationItem>
        );
      }

      return null;
    });
  };

  // Filter sidebar component
  const FilterSidebar = () => (
    <div className="bg-card rounded-xl p-2 shadow-sm border border-border space-y-2">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-primary/10 rounded-lg">
          <Landmark className="h-5 w-5 text-primary" />
        </div>
        <h2 className="text-xl font-bold text-foreground">{t('monuments.filters')}</h2>
      </div>

      {/* Search Filter */}
      <div>
        <h3 className="text-lg font-semibold mb-2">{t('monuments.search')} :</h3>
        <form onSubmit={handleSearch}>
          <div className="relative">
            <Input
              placeholder={t('monuments.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pr-10"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-3 top-3"
              >
                <X className="h-4 w-4 text-gray-400" />
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Destination Filter */}
      <div>
        <h3 className="text-lg font-semibold mb-2">{t('monuments.destination')} :</h3>
        <Select
          value={selectedDestination}
          onValueChange={handleDestinationChange}
          disabled={loadingFilters}
        >
          <SelectTrigger className="w-full flex items-center">
            <SelectValue placeholder={t('monuments.allDestinations')}>
              {selectedDestination !== "all"
                ? getSelectedDestinationName()
                : t('monuments.allDestinations')}
            </SelectValue>
            {selectedDestination !== "all" && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  clearDestination();
                }}
                className="ml-2"
              >
                <X className="h-4 w-4 text-gray-400" />
              </button>
            )}
          </SelectTrigger>
          <SelectContent className="max-h-[400px] overflow-y-auto">
            <SelectGroup>
              <SelectItem value="all" className="font-semibold py-3">
                {t('monuments.allDestinations')}
              </SelectItem>
              {filterOptions.destinations.map((destination) => (
                <SelectItem
                  key={destination.id}
                  value={destination.slug}
                  className="py-2 text-sm text-gray-600"
                >
                  {destination.name}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>

      {/* Active Filters */}
      {(selectedDestination !== "all" || searchQuery) && (
        <div className="pt-4 border-t space-y-4">
          <h3 className="text-lg font-semibold">{t('monuments.activeFilters')}</h3>
          <div className="space-y-2">
            {searchQuery && (
              <div className="flex items-center justify-between bg-blue-50 px-3 py-2 rounded-md">
                <span className="text-sm text-blue-700">
                  {t('monuments.search')}: "{searchQuery}"
                </span>
                <button
                  onClick={clearSearch}
                  className="text-blue-500 hover:text-blue-700"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
            {selectedDestination !== "all" && (
              <div className="flex items-center justify-between bg-green-50 px-3 py-2 rounded-md">
                <span className="text-sm text-green-700">
                  {t('monuments.destination')}: {getSelectedDestinationName()}
                </span>
                <button
                  onClick={clearDestination}
                  className="text-green-500 hover:text-green-700"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={clearFilters}
            className="w-full"
          >
            {t('monuments.clearAllFilters')}
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
      <HeaderPage
        title={t('navigation.monuments')}
        breadcrumbs={[
          { label: t('common.home'), href: "/" },
          { label: t('navigation.monuments'), href: "/monuments" },
        ]}
      />
      
      <div className="container mx-auto px-4 py-8">
        {/* Mobile Filter Button and Active Filters */}
        <div className="lg:hidden mb-6">
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="outline"
              onClick={() => setShowMobileFilters(!showMobileFilters)}
              className="flex items-center gap-2"
            >
              <Filter className="h-4 w-4" />
              {t('monuments.filters')}
              {showMobileFilters ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
            
            {/* Active filters chips for mobile */}
            <div className="flex gap-2 overflow-x-auto">
              {selectedDestination !== "all" && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
                  {getSelectedDestinationName()}
                  <X className="h-3 w-3 ml-1 cursor-pointer" onClick={clearDestination} />
                </span>
              )}
            </div>
          </div>

          {/* Mobile Search */}
          {/* <Card className="mb-4">
            <CardContent className="p-4">
              <form onSubmit={handleSearch} className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder={t('monuments.searchPlaceholderMobile')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="border-gray-300"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={clearSearch}
                      className="absolute right-3 top-3"
                    >
                      <X className="h-4 w-4 text-gray-400" />
                    </button>
                  )}
                </div>
                <Button type="submit">
                  <Search className="h-4 w-4" />
                </Button>
              </form>
            </CardContent>
          </Card> */}

          {/* Mobile Filters Dropdown */}
          {showMobileFilters && (
            <Card className="mb-4">
              <CardContent className="p-4">
                <FilterSidebar />
              </CardContent>
            </Card>
          )}
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left Sidebar - Filters (Desktop) */}
          <div className="hidden lg:block lg:w-1/4 border-r-2 border-gray-200 pr-8">
            <div className="sticky top-24">
              <FilterSidebar />
            </div>
          </div>

          {/* Right Content - Monuments List */}
          <div className="lg:w-3/4">
            {/* Desktop Search */}
            <div className="hidden lg:block mb-6">
              <form onSubmit={handleSearch} className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder={t('monuments.searchPlaceholderDesktop')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 pr-10"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2"
                  >
                    <X className="h-4 w-4 text-gray-400" />
                  </button>
                )}
              </form>
            </div>

            {/* Results Count */}
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-foreground">
                {loading ? t('monuments.loadingMonuments') : `${t('monuments.found')} ${paginationData?.total || 0} ${t('monuments.monumentsCount')}`}
              </h2>
              {paginationData && !loading && (
                <p className="text-gray-600">
                  {t('monuments.showing')} {paginationData.from} {t('monuments.to')} {paginationData.to} {t('monuments.of')} {paginationData.total} {t('monuments.results')}
                  {paginationData.current_page && ` (${t('monuments.page')} ${paginationData.current_page} ${t('monuments.of')} ${paginationData.last_page})`}
                </p>
              )}
            </div>
            
            {/* Monuments Grid */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i}>
                    <Skeleton className="h-48 w-full" />
                    <div className="p-4">
                      <Skeleton className="h-6 w-3/4 mb-2" />
                      <Skeleton className="h-4 w-full mb-2" />
                      <Skeleton className="h-4 w-2/3 mb-4" />
                      <div className="flex justify-between">
                        <Skeleton className="h-4 w-20" />
                        <Skeleton className="h-4 w-16" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : monuments.length === 0 ? (
              <div className="flex flex-col gap-1 justify-center items-center">
                <div className="text-gray-400 mb-4">
                  <Landmark className="h-16 w-16 mx-auto" />
                </div>
                <h3 className="text-xl font-semibold text-gray-600 mb-2">{t('monuments.noMonumentsFound')}</h3>
                <p className="text-gray-500 mb-6">{t('monuments.tryAdjustingFilters')}</p>
                <Button onClick={clearFilters}>{t('monuments.clearAllFilters')}</Button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
                  {monuments.map((monument) => (
                    <MonumentCard
                      key={monument.id}
                      item={monument}
                    />
                  ))}
                </div>
                
                {/* Pagination */}
                {paginationData && paginationData.last_page > 1 && (
                  <div className="mt-8" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
                    <Pagination>
                      <PaginationContent className={locale === 'ar' ? 'flex-row-reverse' : ''}>
                        {renderPaginationLinks()}
                      </PaginationContent>
                    </Pagination>
                    
                    <div className="mt-4 text-center text-sm text-gray-600">
                      <p>
                        {t('monuments.page')} {paginationData.current_page} {t('monuments.of')} {paginationData.last_page}
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}